import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import { authClient } from '../lib/authClient';

function ResetPasswordPage() {
  const { isDarkMode, toggleTheme, themeClasses } = useTheme();
  const { session, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const [language, setLanguage] = useState(() => localStorage.getItem('language') || 'english');
  const direction = language === 'hebrew' ? 'rtl' : 'ltr';

  const isRecoveryCallback = typeof window !== 'undefined' && window.location.hash && /(access_token|refresh_token|type=recovery)/i.test(window.location.hash);

  useEffect(() => {
    if (!isRecoveryCallback) return;
    try { authClient.consumeHashSession(); } catch (_) { /* ignore */ }
  }, [isRecoveryCallback]);

  useEffect(() => {
    if (authLoading) return;
    if (!isRecoveryCallback && !session) return;
    if (session && success) return;
    if (session && isRecoveryCallback && window.location.hash) {
      window.history.replaceState(null, '', window.location.pathname + window.location.search);
    }
  }, [session, authLoading, isRecoveryCallback, success]);

  useEffect(() => {
    const handleStorageChange = () => {
      const newLanguage = localStorage.getItem('language') || 'english';
      setLanguage(newLanguage);
      document.documentElement.dir = newLanguage === 'hebrew' ? 'rtl' : 'ltr';
      document.documentElement.lang = newLanguage === 'hebrew' ? 'he' : 'en';
    };
    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('languageChange', handleStorageChange);
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('languageChange', handleStorageChange);
    };
  }, []);

  const toggleLanguage = () => {
    const newLanguage = language === 'english' ? 'hebrew' : 'english';
    localStorage.setItem('language', newLanguage);
    setLanguage(newLanguage);
    document.documentElement.dir = newLanguage === 'hebrew' ? 'rtl' : 'ltr';
    document.documentElement.lang = newLanguage === 'hebrew' ? 'he' : 'en';
    window.dispatchEvent(new Event('languageChange'));
  };

  const translations = {
    english: {
      'reset.title': 'Set new password',
      'reset.subtitle': 'Enter your new password below.',
      'reset.password': 'New password',
      'reset.confirmPassword': 'Confirm password',
      'reset.submit': 'Update password',
      'reset.updating': 'Updating…',
      'reset.success': 'Password updated. Redirecting to sign in…',
      'reset.backToLogin': 'Back to sign in',
      'reset.error': 'Something went wrong. Please try again.',
      'reset.errorPasswordMatch': 'Passwords do not match.',
      'reset.errorMinLength': 'Password must be at least 6 characters.',
      'reset.invalidLink': 'Invalid or expired reset link. Please request a new one.',
      'reset.requestNewLink': 'Request new link',
      'reset.confirmingLink': 'Confirming your link…',
      'brand.name': 'BetterChoice',
      'brand.portal': 'Dietitian Portal',
      'footer.copyright': '© 2026 BetterChoice. All rights reserved.',
    },
    hebrew: {
      'reset.title': 'הגדר סיסמה חדשה',
      'reset.subtitle': 'הזן את הסיסמה החדשה שלך למטה.',
      'reset.password': 'סיסמה חדשה',
      'reset.confirmPassword': 'אימות סיסמה',
      'reset.submit': 'עדכן סיסמה',
      'reset.updating': 'מעדכן…',
      'reset.success': 'הסיסמה עודכנה. מעביר להתחברות…',
      'reset.backToLogin': 'חזרה להתחברות',
      'reset.error': 'משהו השתבש. אנא נסה שוב.',
      'reset.errorPasswordMatch': 'הסיסמאות אינן תואמות.',
      'reset.errorMinLength': 'הסיסמה חייבת להכיל לפחות 6 תווים.',
      'reset.invalidLink': 'קישור איפוס לא תקין או שפג תוקפו. אנא בקש קישור חדש.',
      'reset.requestNewLink': 'בקש קישור חדש',
      'reset.confirmingLink': 'מאשר את הקישור…',
      'brand.name': 'BetterChoice',
      'brand.portal': 'פורטל דיאטנים',
      'footer.copyright': '© 2026 BetterChoice. כל הזכויות שמורות.',
    },
  };
  const t = (key) => translations[language]?.[key] || key;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (password !== confirmPassword) {
      return setError(t('reset.errorPasswordMatch'));
    }
    if (password.length < 6) {
      return setError(t('reset.errorMinLength'));
    }
    setIsLoading(true);
    try {
      const { error: updateError } = await authClient.updateUser({ password });
      if (updateError) throw new Error(updateError.message);
      await authClient.signOut();
      setSuccess(true);
      setTimeout(() => {
        navigate('/', { state: { passwordReset: true }, replace: true });
      }, 1500);
    } catch (err) {
      console.error('Reset password error:', err);
      setError(err.message || t('reset.error'));
    } finally {
      setIsLoading(false);
    }
  };

  const inputClasses = `w-full px-4 py-3 ${isDarkMode ? 'bg-moss/50 border-line text-white' : 'bg-canvas border-line text-ink'} border rounded-lg placeholder-ink-muted focus:outline-none focus:ring-2 focus:ring-forest focus:border-transparent transition-all`;
  const labelClasses = `block text-sm font-medium ${themeClasses.textSecondary} mb-2`;

  const waitingForSession = isRecoveryCallback && !session;

  if (!authLoading && !isRecoveryCallback && !session) {
    return (
      <div className={`min-h-screen ${themeClasses.bgPrimary} flex items-center justify-center px-4 py-12`} dir={direction}>
        <div className="w-full max-w-md text-center">
          <div className={`${themeClasses.bgCard} rounded-lg p-8 border ${themeClasses.borderPrimary}`}>
            <p className={`${themeClasses.textSecondary} mb-6`}>{t('reset.invalidLink')}</p>
            <button
              type="button"
              onClick={() => navigate('/forgot-password')}
              className={`${themeClasses.btnPrimary} text-white font-semibold py-2 px-4 rounded-lg`}
            >
              {t('reset.requestNewLink')}
            </button>
            <div className="mt-4">
              <button type="button" onClick={() => navigate('/')} className={`text-sm ${isDarkMode ? 'text-forest-ondark' : 'text-forest-ondark'}`}>
                {t('reset.backToLogin')}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`min-h-screen ${themeClasses.bgPrimary} flex items-center justify-center px-4 py-12 transition-colors duration-300`}
      dir={direction}
    >
      <div className="fixed top-4 right-4 z-50 flex gap-2">
        <button
          onClick={toggleTheme}
          className={`${isDarkMode ? 'bg-surface' : 'bg-surface'} text-ink dark:text-ink-dark hover:bg-moss dark:hover:bg-moss-dark p-3 rounded-lg   transition-all duration-300 border ${isDarkMode ? 'border-forest/20' : 'border-forest/30'}`}
          aria-label={isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'}
        >
          {isDarkMode ? (
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
            </svg>
          ) : (
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
            </svg>
          )}
        </button>
        <button
          onClick={toggleLanguage}
          className={`${isDarkMode ? 'bg-surface' : 'bg-surface'} ${isDarkMode ? 'text-forest' : 'text-forest-ondark'} p-3 rounded-lg   transition-all duration-300 border ${isDarkMode ? 'border-forest/20' : 'border-forest/30'} font-semibold`}
          aria-label={language === 'english' ? 'Switch to Hebrew' : 'Switch to English'}
        >
          {language === 'english' ? 'ע' : 'EN'}
        </button>
      </div>

      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-24 h-24 mb-4">
            <img src="/logo.png" alt="BetterChoice Logo" className="w-full h-full object-contain" />
          </div>
          <h1 className={`text-4xl font-bold ${themeClasses.textPrimary} mb-2`}>{t('brand.name')}</h1>
          <p className={`${isDarkMode ? 'text-forest' : 'text-forest-ondark'} text-lg font-medium`}>{t('brand.portal')}</p>
        </div>

        <div className={`${themeClasses.bgCard}  rounded-lg ${themeClasses.shadowCard} p-8 border ${themeClasses.borderPrimary}`}>
          <h2 className={`text-2xl font-semibold ${themeClasses.textPrimary} mb-2 text-center`}>{t('reset.title')}</h2>
          <p className={`text-sm ${themeClasses.textSecondary} mb-6 text-center`}>{t('reset.subtitle')}</p>

          {error && (
            <div className={`mb-4 p-3 ${isDarkMode ? 'bg-red-500/20 border-red-500/50 text-red-300' : 'bg-red-100 border-red-300 text-red-700'} border rounded-lg text-sm`}>
              {error}
            </div>
          )}
          {success && (
            <div className={`mb-4 p-3 ${isDarkMode ? 'bg-forest/20 border-forest/50 text-forest' : 'bg-forest-faint border-forest/40 text-forest-ondark'} border rounded-lg text-sm`}>
              {t('reset.success')}
            </div>
          )}

          {waitingForSession && (
            <div className="flex flex-col items-center py-6">
              <svg className="animate-spin h-10 w-10 text-forest-ondark mb-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
              <p className={themeClasses.textSecondary}>{t('reset.confirmingLink')}</p>
            </div>
          )}

          {session && !success && (
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label htmlFor="password" className={labelClasses}>{t('reset.password')}</label>
                <input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={6}
                  className={inputClasses}
                  placeholder="••••••••"
                />
              </div>
              <div>
                <label htmlFor="confirmPassword" className={labelClasses}>{t('reset.confirmPassword')}</label>
                <input
                  id="confirmPassword"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  minLength={6}
                  className={inputClasses}
                  placeholder="••••••••"
                />
              </div>
              <button
                type="submit"
                disabled={isLoading}
                className={`w-full ${themeClasses.btnPrimary} text-white font-semibold py-3 px-4 rounded-lg transition-all duration-300   disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                {isLoading ? t('reset.updating') : t('reset.submit')}
              </button>
            </form>
          )}

          <div className="mt-6 text-center">
            <button
              type="button"
              onClick={() => navigate('/')}
              className={`text-sm font-semibold ${isDarkMode ? 'text-forest-ondark hover:text-forest' : 'text-forest-ondark hover:text-forest-ondark'} transition-colors`}
            >
              {t('reset.backToLogin')}
            </button>
          </div>
        </div>

        <div className={`mt-8 text-center text-sm ${themeClasses.textMuted}`}>
          <p>{t('footer.copyright')}</p>
        </div>
      </div>
    </div>
  );
}

export default ResetPasswordPage;
